import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Building2, Plus, Pencil, Trash2, Home, MapPin, Calendar, Users, Image, X } from "lucide-react";

export default function AdminPropertyList() {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    location_address: "",
    type_of_construction: "",
    number_of_units: "",
    date_completed: "",
    units: [{ unit_number: "1", unit_type: "" }]
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, [token]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      location_address: "",
      type_of_construction: "",
      number_of_units: "",
      date_completed: "",
      units: [{ unit_number: "1", unit_type: "" }]
    });
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUnitChange = (index, field, value) => {
    const newUnits = [...formData.units];
    newUnits[index][field] = value;
    setFormData({ ...formData, units: newUnits });
  };

  const addUnit = () => {
    const nextUnitNumber = (formData.units.length + 1).toString();
    setFormData({
      ...formData,
      units: [...formData.units, { unit_number: nextUnitNumber, unit_type: "" }]
    });
  };

  const removeUnit = (index) => {
    if (formData.units.length > 1) {
      const newUnits = formData.units.filter((_, i) => i !== index);
      setFormData({ ...formData, units: newUnits });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("location_address", formData.location_address);
      data.append("type_of_construction", formData.type_of_construction);
      data.append("number_of_units", formData.units.length);
      data.append("date_completed", formData.date_completed || "");
      data.append("units_json", JSON.stringify(formData.units));
      if (photo) {
        data.append("photo", photo);
      }

      await axios.post(`${API}/admin/properties`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Property created successfully");
      setShowAddDialog(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error("Failed to create property:", error);
      toast.error(error.response?.data?.detail || "Failed to create property");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("location_address", formData.location_address);
      data.append("type_of_construction", formData.type_of_construction);
      data.append("number_of_units", formData.units.length);
      data.append("date_completed", formData.date_completed || "");
      data.append("units_json", JSON.stringify(formData.units));
      if (photo) {
        data.append("photo", photo);
      }

      await axios.put(`${API}/admin/properties/${selectedProperty.id}`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Property updated successfully");
      setShowEditDialog(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error("Failed to update property:", error);
      toast.error(error.response?.data?.detail || "Failed to update property");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/properties/${selectedProperty.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property deleted successfully");
      setShowDeleteDialog(false);
      fetchProperties();
    } catch (error) {
      console.error("Failed to delete property:", error);
      toast.error("Failed to delete property");
    }
  };

  const openEditDialog = (property) => {
    setSelectedProperty(property);
    setFormData({
      name: property.name || "",
      location_address: property.location_address || "",
      type_of_construction: property.type_of_construction || "",
      number_of_units: property.number_of_units || "",
      date_completed: property.date_completed || "",
      units: property.units || [{ unit_number: "1", unit_type: "" }]
    });
    if (property.photo_url) {
      setPhotoPreview(`${process.env.REACT_APP_BACKEND_URL}${property.photo_url}`);
    }
    setShowEditDialog(true);
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-property-list-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-primary">Property List</h1>
                <p className="text-text-muted">Manage your properties and houses</p>
              </div>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowAddDialog(true); }}
              className="bg-primary hover:bg-primary-800"
              data-testid="add-property-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </div>

          {/* Properties Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-pulse text-primary">Loading properties...</div>
            </div>
          ) : properties.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="w-16 h-16 text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-600 mb-2">No Properties Yet</h3>
                <p className="text-text-muted text-center mb-4">
                  Add your first property to start managing tenants
                </p>
                <Button 
                  onClick={() => { resetForm(); setShowAddDialog(true); }}
                  className="bg-primary hover:bg-primary-800"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Property
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((property) => (
                <Card key={property.id} className="hover:shadow-lg transition-shadow" data-testid={`property-card-${property.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-primary line-clamp-1">
                          {property.name}
                        </CardTitle>
                        <div className="flex items-center gap-1 mt-1 text-sm text-text-muted">
                          <MapPin className="w-3 h-3" />
                          <span className="line-clamp-1">{property.location_address}</span>
                        </div>
                      </div>
                      <Badge className="bg-primary/10 text-primary ml-2">
                        {property.number_of_units} Units
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Property Photo */}
                    <div className="aspect-video rounded-lg bg-stone-100 mb-4 overflow-hidden">
                      {property.photo_url ? (
                        <img 
                          src={getPhotoUrl(property.photo_url)} 
                          alt={property.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Home className="w-12 h-12 text-stone-300" />
                        </div>
                      )}
                    </div>

                    {/* Property Info */}
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-stone-400" />
                        <span className="text-stone-600">{property.type_of_construction}</span>
                      </div>
                      {property.date_completed && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-stone-400" />
                          <span className="text-stone-600">Completed: {property.date_completed}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(property)}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => { setSelectedProperty(property); setShowDeleteDialog(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Property Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Add New Property
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Photo Upload */}
              <div className="space-y-2">
                <Label>Property Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-24 rounded-lg bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center overflow-hidden">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-8 h-8 text-stone-400" />
                    )}
                  </div>
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="max-w-[200px]"
                    />
                    <p className="text-xs text-text-muted mt-1">Upload a photo of the property</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Property Name *</Label>
                  <Input
                    placeholder="e.g., ILE-TUN TUN, IBADAN"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Location Address *</Label>
                  <Input
                    placeholder="e.g., Aina Close, Ile-TunTun, Ibadan"
                    value={formData.location_address}
                    onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Type of Construction *</Label>
                  <Input
                    placeholder="e.g., Three Bedroom Apartments"
                    value={formData.type_of_construction}
                    onChange={(e) => setFormData({ ...formData, type_of_construction: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date Completed</Label>
                  <Input
                    type="date"
                    value={formData.date_completed}
                    onChange={(e) => setFormData({ ...formData, date_completed: e.target.value })}
                  />
                </div>
              </div>

              {/* Units Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Units / Apartments *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addUnit}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Unit
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {formData.units.map((unit, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Unit #"
                          value={unit.unit_number}
                          onChange={(e) => handleUnitChange(index, "unit_number", e.target.value)}
                          className="h-9"
                        />
                        <Input
                          placeholder="Type (e.g., Three-Bedroom)"
                          value={unit.unit_type}
                          onChange={(e) => handleUnitChange(index, "unit_type", e.target.value)}
                          className="h-9"
                        />
                      </div>
                      {formData.units.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUnit(index)}
                          className="h-9 w-9 p-0 text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-text-muted">
                  {formData.units.length} unit(s) defined
                </p>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary-800" disabled={submitting}>
                {submitting ? "Creating..." : "Create Property"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit Property
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4">
              {/* Photo Upload */}
              <div className="space-y-2">
                <Label>Property Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-24 rounded-lg bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center overflow-hidden">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-8 h-8 text-stone-400" />
                    )}
                  </div>
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="max-w-[200px]"
                    />
                    <p className="text-xs text-text-muted mt-1">Upload a new photo to replace</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Property Name *</Label>
                  <Input
                    placeholder="e.g., ILE-TUN TUN, IBADAN"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Location Address *</Label>
                  <Input
                    placeholder="e.g., Aina Close, Ile-TunTun, Ibadan"
                    value={formData.location_address}
                    onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Type of Construction *</Label>
                  <Input
                    placeholder="e.g., Three Bedroom Apartments"
                    value={formData.type_of_construction}
                    onChange={(e) => setFormData({ ...formData, type_of_construction: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date Completed</Label>
                  <Input
                    type="date"
                    value={formData.date_completed}
                    onChange={(e) => setFormData({ ...formData, date_completed: e.target.value })}
                  />
                </div>
              </div>

              {/* Units Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Units / Apartments *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addUnit}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Unit
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {formData.units.map((unit, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Unit #"
                          value={unit.unit_number}
                          onChange={(e) => handleUnitChange(index, "unit_number", e.target.value)}
                          className="h-9"
                        />
                        <Input
                          placeholder="Type (e.g., Three-Bedroom)"
                          value={unit.unit_type}
                          onChange={(e) => handleUnitChange(index, "unit_type", e.target.value)}
                          className="h-9"
                        />
                      </div>
                      {formData.units.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUnit(index)}
                          className="h-9 w-9 p-0 text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-text-muted">
                  {formData.units.length} unit(s) defined
                </p>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary-800" disabled={submitting}>
                {submitting ? "Updating..." : "Update Property"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Property</DialogTitle>
          </DialogHeader>
          <p className="text-stone-600">
            Are you sure you want to delete <strong>{selectedProperty?.name}</strong>? 
            This will also delete all tenant records for this property. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleDelete}
            >
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
